import type { CSSProperties } from 'react'

import { useTheme } from '../lib/theme'

/**
 * The Talari mark.
 *
 * This is the supplied artwork, not a redrawing of it. An earlier version of
 * this file drew a cloud as an SVG path — it was the wrong mark: the real one
 * carries the wordmark inside the cloud rather than beside it, and no amount
 * of curve-fitting was going to arrive at someone else's logotype.
 *
 * It ships as two PNGs, one per theme, cut from the original by
 * `scripts/extract_logo.py`. The obvious alternative is one file used as a CSS
 * mask, which would take `currentColor` and adapt on its own — but masks
 * rendered as nothing when tested here, and a technique that cannot be checked
 * is not one to ship. Two images always work, at the cost of one extra file.
 *
 * The artwork carries a wordmark of its own, and "TalariCloud" is still set
 * beside it. That is deliberate rather than an oversight: the drawn letterforms
 * are a picture, so they are not selectable, not searchable, and not read out;
 * and the artwork's own first letter is ambiguous enough that the name is worth
 * stating plainly. `Logo` is the lockup — mark plus name — and is what call
 * sites want. `Mark` is the bare artwork, for the rare place a lockup does not
 * fit.
 */

type Props = {
  /** Rendered height in pixels. The artwork is 1.64× as wide as it is tall. */
  size?: number
  className?: string
  /** Screen-reader text. Empty where the name is already adjacent. */
  alt?: string
}

/** How much wider than tall the extracted artwork is: 551×337 after
 *  `extract_logo.py` trims it to the ink. */
const ASPECT = 1.635

export function Mark({ size = 28, className, alt = '' }: Props) {
  const { theme } = useTheme()
  return (
    <img
      className={`app-logo ${className ?? ''}`}
      /* Cache-busted: the filename is new, but the icons beside it are not,
         and a stale mark next to a fresh favicon is worse than either. */
      src={theme === 'dark' ? '/talari-mark-dark.png?v=3' : '/talari-mark.png?v=3'}
      width={Math.round(size * ASPECT)}
      height={size}
      alt={alt}
      /* Both dimensions are given so the row does not reflow while it loads,
         which on the sign-in page is the first thing anyone sees. */
      decoding="async"
    />
  )
}

/**
 * The full lockup: the mark with "TalariCloud" beside it.
 *
 * `size` stays the mark's height and the name is sized from it, so a call site
 * passes one number and the proportions hold at every scale.
 */
export function Logo({ size = 28, className }: Props) {
  return (
    <span
      className={`brand-lockup ${className ?? ''}`}
      /* The type scales off the mark rather than being set per call site, so
         the two cannot drift apart at one size and not another. */
      style={{ '--mark-size': `${size}px` } as CSSProperties}
    >
      {/* alt is empty because the name follows as real text; giving the image
          the same alt would have a screen reader say it twice. */}
      <Mark size={size} alt="" />
      {/* One word, two colours — not two words. Kept in a single element with
          no whitespace between the spans so it stays one selectable,
          searchable string and a screen reader reads "TalariCloud". */}
      <span className="wordmark">
        Talari<span className="wordmark-cloud">Cloud</span>
      </span>
    </span>
  )
}

export default Logo
